import type { ComponentType, SVGProps } from "react";
import {
  IconBuilding,
  IconHardHat,
  IconHome,
  IconKey,
  IconLeaf,
  IconStore,
  IconWindow,
} from "../components/Icon";

export type Service = {
  id: string;
  slug: string;
  title: string;
  short: string;
  long: string;
  bullets: string[];
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  keywords: string[];
  /** URL de la page dédiée si elle existe (sinon lien par ancre vers /services#id) */
  pageHref?: string;
};

export const services: Service[] = [
  {
    id: "bureaux",
    slug: "nettoyage-bureaux",
    title: "Nettoyage de bureaux",
    short:
      "Entretien régulier de vos bureaux à Vichy : sols, sanitaires, salles de réunion, postes de travail.",
    long:
      "Nous assurons la propreté quotidienne de vos espaces professionnels à Vichy et dans l'Allier, avec des prestations planifiées en dehors des heures d'activité et dans le respect de la discrétion.",
    bullets: [
      "Dépoussiérage postes, écrans et mobilier",
      "Désinfection des points de contact",
      "Sanitaires, cuisines et salles de pause",
      "Aspiration et lavage des sols",
      "Gestion des déchets et recyclage",
    ],
    icon: IconBuilding,
    keywords: ["nettoyage bureaux Vichy", "entretien locaux professionnels Allier"],
  },
  {
    id: "coproprietes",
    slug: "nettoyage-coproprietes",
    title: "Nettoyage de copropriétés",
    short:
      "Entretien des parties communes d'immeubles : halls, escaliers, ascenseurs, locaux poubelles.",
    long:
      "Partenaire des syndics et bailleurs de Vichy, nous maintenons vos résidences impeccables avec un planning clair et un carnet de passage consultable.",
    bullets: [
      "Halls d'entrée, paliers, escaliers",
      "Ascenseurs, miroirs, boîtes aux lettres",
      "Local poubelles et sortie de containers",
      "Vitres parties communes",
      "Désinfection régulière",
    ],
    icon: IconHome,
    keywords: ["nettoyage copropriété Vichy", "entretien parties communes Allier"],
  },
  {
    id: "commerces",
    slug: "nettoyage-commerces",
    title: "Nettoyage de commerces",
    short:
      "Boutiques, restaurants, cabinets : un environnement impeccable pour accueillir vos clients.",
    long:
      "Interventions avant ouverture ou après fermeture pour ne jamais perturber votre activité, avec produits adaptés aux normes d'hygiène.",
    bullets: [
      "Vitrines, comptoirs et surfaces",
      "Sols sensibles (carrelage, parquet, stratifié)",
      "Sanitaires clients et personnel",
      "Vidage corbeilles & réassort consommables",
      "Respect HACCP pour la restauration",
    ],
    icon: IconStore,
    keywords: ["nettoyage commerce Vichy", "nettoyage restaurant Allier"],
  },
  {
    id: "vitres",
    slug: "nettoyage-vitres",
    title: "Nettoyage de vitres",
    short:
      "Vitrines, baies, verrières : des surfaces sans traces, à Vichy et en Bourbonnais.",
    long:
      "Matériel professionnel (mouilleurs, raclettes, perches télescopiques) et techniques eau pure pour un rendu parfait, même en hauteur.",
    bullets: [
      "Vitrines et façades commerciales",
      "Baies vitrées et verrières",
      "Encadrements, rebords et volets",
      "Techniques eau pure sans produits",
      "Interventions ponctuelles ou programmées",
    ],
    icon: IconWindow,
    keywords: ["nettoyage vitres Vichy", "laveur de vitres Allier"],
  },
  {
    id: "airbnb",
    slug: "nettoyage-airbnb-location-courte-duree",
    title: "Nettoyage Airbnb & locations courtes durées",
    short:
      "Remise en état entre deux locations : ménage complet, linge, réassort, check photos.",
    long:
      "Vichy est une ville thermale touristique : les locations de courte durée (Airbnb, Booking, Abritel, Gîtes de France) demandent un standard hôtelier à chaque rotation. Nous assurons un ménage rapide, fiable et documenté entre deux séjours, avec une disponibilité 7j/7 pour coller aux créneaux check-out / check-in.",
    bullets: [
      "Ménage complet entre deux séjours",
      "Changement des draps & serviettes (linge fourni ou loué)",
      "Réassort consommables (café, papier, savon, capsules)",
      "Check photos envoyé à l'hôte après chaque passage",
      "Interventions 7j/7, créneaux 11h-15h respectés",
      "Signalement des dégradations ou objets oubliés",
    ],
    icon: IconKey,
    keywords: [
      "nettoyage Airbnb Vichy",
      "ménage location courte durée Vichy",
      "conciergerie ménage Vichy",
      "nettoyage location saisonnière Allier",
    ],
    pageHref: "/nettoyage-airbnb",
  },
  {
    id: "chantier",
    slug: "nettoyage-fin-de-chantier",
    title: "Nettoyage fin de chantier",
    short:
      "Livraison impeccable après travaux : poussières, traces de plâtre, résidus collés.",
    long:
      "Nous intervenons après artisans et entreprises du bâtiment à Vichy pour une remise en état clé-en-main, avant emménagement ou livraison.",
    bullets: [
      "Dépoussiérage complet murs/plafonds",
      "Décollage traces de peinture et colle",
      "Nettoyage sanitaires et cuisines neufs",
      "Vitrages et encadrements",
      "Sols : premier lavage & cristallisation",
    ],
    icon: IconHardHat,
    keywords: ["nettoyage fin de chantier Vichy", "remise en état après travaux Allier"],
  },
  {
    id: "particuliers",
    slug: "entretien-particuliers",
    title: "Entretien chez particuliers",
    short:
      "Ménage régulier ou ponctuel à domicile, en toute confiance.",
    long:
      "Nous proposons également aux particuliers un service d'entretien régulier, de grand ménage de printemps ou de remise en état avant état des lieux.",
    bullets: [
      "Ménage hebdomadaire ou bi-mensuel",
      "Grand ménage saisonnier",
      "Remise en état locative",
      "Repassage sur demande",
      "Produits écologiques disponibles",
    ],
    icon: IconLeaf,
    keywords: ["femme de ménage Vichy", "ménage à domicile Allier"],
  },
];
